import type { TargetedRangeOp, TreeNode, UpdateResult } from "../types";
import type { Logger } from "../utils/logger";

/**
 * Optional context for `applyUpdate` that opts into per-op targeted DOM
 * mutation. When `canApplyTargeted` returns true for a top-level diff-op
 * key, applyUpdate mutates treeState in place (no deepClone) and emits
 * a `TargetedRangeOp` in the result. The caller is then responsible for
 * applying the op directly to the live DOM (typically via RangeDomApplier).
 *
 * The corresponding range subtree is replaced in `result.html` with a
 * `<!--lvt-targeted-skip:${rangePath}-->` placeholder so that morphdom
 * can be told to short-circuit that subtree.
 */
export interface ApplyUpdateOptions {
  canApplyTargeted?: (rangeStructure: any, rangePath: string) => boolean;
}

interface RangeStateEntry {
  items: any[];
  statics: any[];
}

/**
 * Deep clone an object. Uses structuredClone if available (Node 17+, modern browsers),
 * falls back to JSON.parse/stringify for older environments.
 */
function deepClone<T>(obj: T): T {
  if (typeof structuredClone === "function") {
    return structuredClone(obj);
  }
  return JSON.parse(JSON.stringify(obj));
}

/**
 * Checks if a node is a valid range structure.
 *
 * A "range" in LiveTemplate represents a {{range .Items}}...{{end}} construct.
 * It has:
 * - `d` (dynamics): Array of rendered items
 * - `s` (statics): Array of static HTML fragments between dynamic slots
 *
 * A "non-range" is any other tree node (e.g., an {{else}} clause with simple content).
 *
 * @param node - The tree node to check
 * @returns true if the node has both `d` and `s` arrays (valid range structure)
 */
function isRangeNode(node: any): boolean {
  return (
    node != null &&
    typeof node === "object" &&
    Array.isArray(node.d) &&
    Array.isArray(node.s)
  );
}

/**
 * Checks if a node or any of its nested children contains a range structure.
 *
 * This is needed for detecting structure transitions like:
 * - {{if .ShowList}}<ul>{{range .Items}}...{{end}}</ul>{{else}}<p>Hidden</p>{{end}}
 *
 * When ShowList changes from true to false, the outer node doesn't have `d` directly,
 * but its child at position "0" does. We need to detect this nested range to know
 * that a full replacement is required instead of a merge.
 *
 * @param node - The tree node to check
 * @returns true if this node or any nested child has a range structure
 */
function hasRangeAnywhere(node: any, depth = 0): boolean {
  // Depth limit to prevent potential stack overflow on deeply nested or malformed trees
  const MAX_DEPTH = 50;
  if (depth > MAX_DEPTH || node == null || typeof node !== "object" || Array.isArray(node)) {
    return false;
  }

  // Check if this node itself is a range
  if (isRangeNode(node)) {
    return true;
  }

  // Check numeric keys (dynamic positions) for nested ranges
  for (const key of Object.keys(node)) {
    if (/^\d+$/.test(key)) {
      const child = node[key];
      if (child != null && typeof child === "object" && !Array.isArray(child)) {
        if (hasRangeAnywhere(child, depth + 1)) {
          return true;
        }
      }
    }
  }

  return false;
}

/**
 * Determines if a structure transition requires full replacement instead of merge.
 *
 * This handles cases where:
 * 1. Old structure has a range (directly or nested) but new structure doesn't
 * 2. New structure has statics (indicating a complete structure definition)
 * 3. Old structure has dynamics that new structure doesn't have
 *
 * @param existing - The existing tree node
 * @param update - The update tree node
 * @returns true if the update should fully replace existing instead of merging
 */
function shouldFullReplace(existing: any, update: any): boolean {
  // If update doesn't have statics, it's a partial update, not a replacement
  if (!update.s || !Array.isArray(update.s)) {
    return false;
  }

  // Check for range→non-range transition (including nested ranges)
  if (hasRangeAnywhere(existing) && !hasRangeAnywhere(update)) {
    return true;
  }

  // Check if existing has dynamics that update doesn't have
  for (const key of Object.keys(existing)) {
    if (/^\d+$/.test(key) && !(key in update)) {
      // Existing has a dynamic position that update doesn't
      // If update has statics, this is a structure change
      return true;
    }
  }

  return false;
}

/**
 * Handles tree state management and HTML reconstruction logic for LiveTemplate.
 */
export class TreeRenderer {
  private treeState: TreeNode = {};
  private rangeState: Record<string, RangeStateEntry> = {};
  private rangeIdKeys: Record<string, string> = {};

  constructor(private readonly logger: Logger) {}

  applyUpdate(
    update: TreeNode,
    opts?: ApplyUpdateOptions
  ): UpdateResult {
    let changed = false;
    const targetedOps: TargetedRangeOp[] = [];
    const skipPaths = new Set<string>();

    for (const [key, value] of Object.entries(update)) {
      const isDifferentialOps =
        Array.isArray(value) &&
        value.length > 0 &&
        Array.isArray(value[0]) &&
        typeof value[0][0] === "string";

      if (isDifferentialOps) {
        // Check if there's an existing range structure to apply operations to
        const existing = this.treeState[key];
        const existingIsRange =
          existing &&
          typeof existing === "object" &&
          !Array.isArray(existing) &&
          Array.isArray(existing.d) &&
          Array.isArray(existing.s);

        if (existingIsRange) {
          const targetedEligible =
            opts?.canApplyTargeted?.(existing, key) === true;
          if (targetedEligible) {
            // Mutate in place — applyDifferentialOpsToRange uses splice/push/unshift
            // on existing.d, so no fresh array is created and treeState[key]
            // remains the same object reference (now with updated contents).
            this.applyDifferentialOpsToRange(existing, value, key);
            targetedOps.push({
              rangePath: key,
              ops: value,
              statics: existing.s,
              idKey: existing.m?.idKey,
            });
            skipPaths.add(key);
          } else {
            // Apply differential operations to a clone (defensive, fallback path)
            this.treeState[key] = deepClone(existing);
            this.applyDifferentialOpsToRange(this.treeState[key], value, key);
          }
        } else {
          // No existing range, store operations directly (will use rangeState later)
          this.treeState[key] = value;
        }
        changed = true;
      } else {
        const oldValue = this.treeState[key];
        const newValue =
          typeof value === "object" && value !== null && !Array.isArray(value)
            ? this.deepMergeTreeNodes(oldValue, value, key)
            : value;

        if (JSON.stringify(oldValue) !== JSON.stringify(newValue)) {
          this.treeState[key] = newValue;
          changed = true;
        }
      }
    }

    const html = this.reconstructFromTree(
      this.treeState,
      "",
      skipPaths.size > 0 ? skipPaths : undefined
    );
    const result: UpdateResult = { html, changed };
    if (targetedOps.length > 0) {
      result.targetedOps = targetedOps;
    }
    return result;
  }

  reset(): void {
    this.treeState = {};
    this.rangeState = {};
    this.rangeIdKeys = {};
  }

  getTreeState(): TreeNode {
    return { ...this.treeState };
  }

  getStaticStructure(): string[] | null {
    return this.treeState.s || null;
  }

  /**
   * Re-render the current treeState as full HTML, with no targeted-apply
   * skip placeholders. Used by `LiveTemplateClient.updateDOM` as a fallback
   * when one or more targeted-apply DOM mutations failed: the treeState is
   * authoritative, so a full rebuild + morphdom pass restores consistency.
   */
  renderState(): string {
    return this.reconstructFromTree(this.treeState, "");
  }

  private deepMergeTreeNodes(
    existing: any,
    update: any,
    currentPath: string = ""
  ): any {
    if (
      typeof update !== "object" ||
      update === null ||
      Array.isArray(update)
    ) {
      return update;
    }

    if (
      typeof existing !== "object" ||
      existing === null ||
      Array.isArray(existing)
    ) {
      return update;
    }

    // Detect structure transitions that require full replacement instead of merge.
    // This handles:
    // 1. Direct range→non-range: existing has d/s arrays, update doesn't
    // 2. Nested range→non-range: existing has range in a child, update doesn't
    // 3. Structure changes: existing has dynamics that update doesn't have
    //
    // Without this check, old range data would be preserved and mixed with
    // new statics, causing wrong content or memory leaks.
    if (shouldFullReplace(existing, update)) {
      this.logger.debug(
        `[deepMerge] Structure transition at path ${currentPath}, replacing instead of merging`
      );
      return update;
    }

    const merged: any = { ...existing };

    for (const [key, value] of Object.entries(update)) {
      const fieldPath = currentPath ? `${currentPath}.${key}` : key;

      // Check if value is a differential operations array
      const isDifferentialOps =
        Array.isArray(value) &&
        value.length > 0 &&
        Array.isArray(value[0]) &&
        typeof value[0][0] === "string";

      // Check if existing value is a range structure
      const existingIsRange =
        merged[key] &&
        typeof merged[key] === "object" &&
        !Array.isArray(merged[key]) &&
        Array.isArray(merged[key].d) &&
        Array.isArray(merged[key].s);

      if (isDifferentialOps && existingIsRange) {
        // Deep clone the range structure before modifying to avoid mutating the original
        // (shallow copy {...existing} keeps shared references to nested objects)
        merged[key] = deepClone(merged[key]);
        // Apply differential operations to the cloned range
        this.logger.debug(
          `[deepMerge] Applying diff ops at path ${fieldPath}`,
          { ops: value, rangeItems: merged[key].d?.length }
        );
        this.applyDifferentialOpsToRange(merged[key], value, fieldPath);
      } else if (
        typeof value === "object" &&
        value !== null &&
        !Array.isArray(value) &&
        typeof merged[key] === "object" &&
        merged[key] !== null &&
        !Array.isArray(merged[key])
      ) {
        merged[key] = this.deepMergeTreeNodes(merged[key], value, fieldPath);
      } else {
        merged[key] = value;
      }
    }

    return merged;
  }

  /**
   * Applies differential operations to the provided range structure in-place.
   * The caller is responsible for passing the object to be mutated (typically a clone).
   * This is called when merging nested updates that contain range operations.
   */
  private applyDifferentialOpsToRange(
    rangeStructure: any,
    operations: any[],
    statePath: string
  ): void {
    // Validate rangeStructure before proceeding
    if (
      !rangeStructure ||
      typeof rangeStructure !== "object" ||
      !Array.isArray(rangeStructure.d) ||
      !Array.isArray(rangeStructure.s)
    ) {
      this.logger.error(
        `[applyDiffOpsToRange] Invalid rangeStructure at path ${statePath}`,
        { rangeStructure }
      );
      return;
    }

    const currentItems = rangeStructure.d;

    // Ensure rangeState is synchronized
    if (!this.rangeState[statePath]) {
      this.rangeState[statePath] = {
        items: currentItems,
        statics: rangeStructure.s,
      };
    }
    // Also check for idKey metadata
    if (
      rangeStructure.m &&
      typeof rangeStructure.m === "object" &&
      typeof rangeStructure.m.idKey === "string"
    ) {
      this.rangeIdKeys[statePath] = rangeStructure.m.idKey;
    }

    this.logger.debug(
      `[applyDiffOpsToRange] path=${statePath}, idKey=${this.rangeIdKeys[statePath]}, items=${currentItems.length}, ops=${operations.length}`
    );

    for (const operation of operations) {
      if (!Array.isArray(operation) || operation.length < 2) {
        continue;
      }

      const opType = operation[0];

      switch (opType) {
        case "r": {
          const key = operation[1];
          const removeIndex = this.findItemIndexByKey(
            currentItems,
            key,
            rangeStructure.s,
            statePath
          );
          this.logger.debug(
            `[applyDiffOpsToRange] Remove: key=${key}, index=${removeIndex}, total=${currentItems.length}`
          );
          if (removeIndex >= 0) {
            currentItems.splice(removeIndex, 1);
            this.logger.debug(`[applyDiffOpsToRange] After removal: ${currentItems.length} items`);
          } else {
            this.logger.debug(`[applyDiffOpsToRange] Remove failed: key ${key} not found`);
          }
          break;
        }
        case "u": {
          const updateIndex = this.findItemIndexByKey(
            currentItems,
            operation[1],
            rangeStructure.s,
            statePath
          );
          const changes = operation[2];
          if (updateIndex >= 0 && changes) {
            currentItems[updateIndex] = this.mergeRangeItem(
              currentItems[updateIndex],
              changes,
              statePath
            );
          }
          break;
        }
        case "a": {
          const itemsToAdd = Array.isArray(operation[1])
            ? operation[1]
            : [operation[1]];
          if (operation[2]) {
            rangeStructure.s = operation[2];
          }
          currentItems.push(...itemsToAdd);
          if (
            operation[3] &&
            typeof operation[3] === "object" &&
            operation[3].idKey
          ) {
            this.rangeIdKeys[statePath] = operation[3].idKey;
          }
          break;
        }
        case "p": {
          const itemsToPrepend = Array.isArray(operation[1])
            ? operation[1]
            : [operation[1]];
          if (operation[2]) {
            rangeStructure.s = operation[2];
          }
          currentItems.unshift(...itemsToPrepend);
          break;
        }
        case "i": {
          const targetIndex = this.findItemIndexByKey(
            currentItems,
            operation[1],
            rangeStructure.s,
            statePath
          );
          if (targetIndex >= 0) {
            const itemsToInsert = Array.isArray(operation[2])
              ? operation[2]
              : [operation[2]];
            currentItems.splice(targetIndex + 1, 0, ...itemsToInsert);
          }
          break;
        }
        case "o": {
          const newOrder = operation[1] as string[];
          const reorderedItems: any[] = [];
          const itemsByKey = new Map<string, any>();

          for (const item of currentItems) {
            const itemKey = this.getItemKey(item, rangeStructure.s, statePath);
            if (itemKey) {
              itemsByKey.set(itemKey, item);
            }
          }

          for (const orderedKey of newOrder) {
            const item = itemsByKey.get(orderedKey);
            if (item) {
              reorderedItems.push(item);
            }
          }

          currentItems.length = 0;
          currentItems.push(...reorderedItems);
          break;
        }
        default:
          break;
      }
    }

    // Update rangeState to reflect the changes
    this.rangeState[statePath] = {
      items: currentItems,
      statics: rangeStructure.s,
    };
  }

  private reconstructFromTree(
    node: TreeNode,
    statePath: string,
    skipPaths?: Set<string>
  ): string {
    if (node.s && Array.isArray(node.s)) {
      let html = "";

      for (let i = 0; i < node.s.length; i++) {
        const staticSegment = node.s[i];
        html += staticSegment;

        if (i < node.s.length - 1) {
          const dynamicKey = i.toString();
          if (node[dynamicKey] !== undefined) {
            const newStatePath = statePath
              ? `${statePath}.${dynamicKey}`
              : dynamicKey;

            // Targeted-apply skip mechanism: emit a comment placeholder that
            // the client's updateDOM walks for, converting to the live
            // container's data-lvt-targeted-skip marker so morphdom can
            // short-circuit the subtree.
            if (skipPaths && skipPaths.has(newStatePath)) {
              html += `<!--lvt-targeted-skip:${newStatePath}-->`;
              continue;
            }

            html += this.renderValue(
              node[dynamicKey],
              dynamicKey,
              newStatePath
            );
          }
        }
      }

      html = html.replace(/<root>/g, "").replace(/<\/root>/g, "");
      return html;
    }

    return this.renderValue(node, "", statePath);
  }

  private renderValue(
    value: any,
    fieldKey?: string,
    statePath?: string
  ): string {
    if (value === null || value === undefined) {
      return "";
    }

    if (
      typeof value === "string" &&
      value.startsWith("{{") &&
      value.endsWith("}}")
    ) {
      return "";
    }

    if (typeof value === "object" && !Array.isArray(value)) {
      if (
        value.d &&
        Array.isArray(value.d) &&
        value.s &&
        Array.isArray(value.s)
      ) {
        const stateKey = statePath || fieldKey || "";
        if (stateKey) {
          this.rangeState[stateKey] = {
            items: value.d,
            statics: value.s,
          };
          if (
            value.m &&
            typeof value.m === "object" &&
            typeof value.m.idKey === "string"
          ) {
            this.rangeIdKeys[stateKey] = value.m.idKey;
          }
        }
        return this.renderRangeStructure(value, fieldKey, statePath);
      }

      if ("s" in value && Array.isArray((value as TreeNode).s)) {
        return this.reconstructFromTree(value as TreeNode, statePath || "");
      }

      // Handle objects with only numeric keys (dynamics without statics)
      // This occurs when server sends partial updates for nested TreeNodes
      const keys = Object.keys(value);
      const numericKeys = keys.filter((k) => /^\d+$/.test(k)).sort((a, b) => parseInt(a) - parseInt(b));
      if (numericKeys.length > 0 && numericKeys.length === keys.length) {
        // All keys are numeric - render each dynamic value in order
        return numericKeys
          .map((k) => {
            const itemStatePath = statePath ? `${statePath}.${k}` : k;
            return this.renderValue((value as Record<string, unknown>)[k], k, itemStatePath);
          })
          .join("");
      }
    }

    if (Array.isArray(value)) {
      if (
        value.length > 0 &&
        Array.isArray(value[0]) &&
        typeof value[0][0] === "string"
      ) {
        return this.applyDifferentialOperations(value, statePath);
      }

      return value
        .map((item, idx) => {
          const itemKey = idx.toString();
          const itemStatePath = statePath ? `${statePath}.${itemKey}` : itemKey;
          if (typeof item === "object" && item && (item as TreeNode).s) {
            return this.reconstructFromTree(item as TreeNode, itemStatePath);
          }
          return this.renderValue(item, itemKey, itemStatePath);
        })
        .join("");
    }

    if (typeof value === "object") {
      // Plain data objects (without tree structure) are state values that shouldn't be rendered.
      // This happens when state contains objects like EditingItem that are used by server-side
      // templates but aren't meant to be rendered directly in the DOM.
      // Skip them silently instead of converting to "[object Object]".
      this.logger.debug(
        "Skipping plain object value (not a tree node) - this is normal for state-only data"
      );
      return "";
    }

    return String(value);
  }

  private renderRangeStructure(
    rangeNode: any,
    fieldKey?: string,
    statePath?: string
  ): string {
    const { d: dynamics, s: statics } = rangeNode;

    if (!dynamics || !Array.isArray(dynamics)) {
      return "";
    }

    if (dynamics.length === 0) {
      if (rangeNode["else"]) {
        const elseKey = "else";
        const elseStatePath = statePath ? `${statePath}.else` : "else";
        return this.renderValue(rangeNode["else"], elseKey, elseStatePath);
      }
      return "";
    }

    if (statics && Array.isArray(statics)) {
      return dynamics
        .map((item: any, itemIdx: number) =>
          this.renderRangeItem(item, itemIdx, statics, statePath)
        )
        .join("");
    }

    return dynamics
      .map((item: any, idx: number) => {
        const itemKey = idx.toString();
        const itemStatePath = statePath ? `${statePath}.${itemKey}` : itemKey;
        return this.renderValue(item, itemKey, itemStatePath);
      })
      .join("");
  }

  /**
   * Renders a single range item to HTML by interleaving its dynamic slots
   * with the range's statics.
   *
   * Used by `renderRangeStructure` and `renderItemsWithStatics` for full
   * range rendering, and by `range-dom-applier` to render a single new
   * or updated item for targeted DOM mutations.
   */
  renderRangeItem(
    item: any,
    itemIdx: number,
    statics: string[],
    statePath?: string
  ): string {
    const itemStatics = statics;

    let html = "";

    for (let i = 0; i < itemStatics.length; i++) {
      html += itemStatics[i];

      if (i < itemStatics.length - 1) {
        const fieldKey = i.toString();
        if (item[fieldKey] !== undefined) {
          const itemStatePath = statePath
            ? `${statePath}.${itemIdx}.${fieldKey}`
            : `${itemIdx}.${fieldKey}`;
          html += this.renderValue(item[fieldKey], fieldKey, itemStatePath);
        }
      }
    }

    return html;
  }

  private applyDifferentialOperations(
    operations: any[],
    statePath?: string
  ): string {
    if (!statePath || !this.rangeState[statePath]) {
      return "";
    }

    const rangeData = this.rangeState[statePath];
    const currentItems = [...rangeData.items];
    const statics = rangeData.statics;

    for (const operation of operations) {
      if (!Array.isArray(operation) || operation.length < 2) {
        continue;
      }

      const opType = operation[0];

      switch (opType) {
        case "r": {
          const removeIndex = this.findItemIndexByKey(
            currentItems,
            operation[1],
            statics,
            statePath
          );
          if (removeIndex >= 0) {
            currentItems.splice(removeIndex, 1);
          }
          break;
        }
        case "u": {
          const updateIndex = this.findItemIndexByKey(
            currentItems,
            operation[1],
            statics,
            statePath
          );
          const changes = operation[2];
          if (updateIndex >= 0 && changes) {
            currentItems[updateIndex] = this.mergeRangeItem(
              currentItems[updateIndex],
              changes,
              statePath || ""
            );
          }
          break;
        }
        case "a": {
          this.addItemsToRange(
            currentItems,
            operation[1],
            operation[2],
            rangeData,
            false
          );
          if (
            operation[3] &&
            typeof operation[3] === "object" &&
            operation[3].idKey
          ) {
            this.rangeIdKeys[statePath || ""] = operation[3].idKey;
          }
          break;
        }
        case "p": {
          this.addItemsToRange(
            currentItems,
            operation[1],
            operation[2],
            rangeData,
            true
          );
          break;
        }
        case "i": {
          const targetIndex = this.findItemIndexByKey(
            currentItems,
            operation[1],
            statics,
            statePath
          );
          if (targetIndex >= 0) {
            const itemsToInsert = Array.isArray(operation[2])
              ? operation[2]
              : [operation[2]];
            currentItems.splice(targetIndex + 1, 0, ...itemsToInsert);
          }
          break;
        }
        case "o": {
          const newOrder = operation[1] as string[];
          const reorderedItems: any[] = [];
          const itemsByKey = new Map<string, any>();

          for (const item of currentItems) {
            const itemKey = this.getItemKey(item, statics, statePath);
            if (itemKey) {
              itemsByKey.set(itemKey, item);
            }
          }

          for (const orderedKey of newOrder) {
            const item = itemsByKey.get(orderedKey);
            if (item) {
              reorderedItems.push(item);
            }
          }

          currentItems.length = 0;
          currentItems.push(...reorderedItems);
          break;
        }
        default:
          break;
      }
    }

    this.rangeState[statePath] = {
      items: currentItems,
      statics: rangeData.statics,
    };

    this.treeState[statePath] = {
      d: currentItems,
      s: rangeData.statics,
    };

    const rangeStructure = this.getCurrentRangeStructure(statePath);
    if (rangeStructure && rangeStructure.s) {
      return this.renderItemsWithStatics(
        currentItems,
        rangeStructure.s,
        statePath
      );
    }

    return currentItems.map((item) => this.renderValue(item)).join("");
  }

  private getCurrentRangeStructure(stateKey: string): any {
    if (this.rangeState[stateKey]) {
      return {
        d: this.rangeState[stateKey].items,
        s: this.rangeState[stateKey].statics,
      };
    }

    const fieldValue = this.treeState[stateKey];
    if (
      fieldValue &&
      typeof fieldValue === "object" &&
      (fieldValue as TreeNode).s
    ) {
      return fieldValue;
    }

    return null;
  }

  private renderItemsWithStatics(
    items: any[],
    statics: string[],
    statePath?: string
  ): string {
    const result = items
      .map((item: any, itemIdx: number) =>
        this.renderRangeItem(item, itemIdx, statics, statePath)
      )
      .join("");

    if (this.logger.isDebugEnabled()) {
      this.logger.debug("[renderItemsWithStatics] statics:", statics);
      this.logger.debug("[renderItemsWithStatics] items count:", items.length);
      this.logger.debug(
        "[renderItemsWithStatics] result snippet:",
        result.substring(0, 200)
      );
    }

    return result;
  }

  private addItemsToRange(
    currentItems: any[],
    items: any,
    statics: any[] | undefined,
    rangeData: RangeStateEntry,
    prepend: boolean
  ): void {
    if (statics) {
      rangeData.statics = statics;
    }

    if (!items) return;

    const itemsArray = Array.isArray(items) ? items : [items];
    if (prepend) {
      currentItems.unshift(...itemsArray);
    } else {
      currentItems.push(...itemsArray);
    }
  }

  private getItemKey(
    item: any,
    statics: any[],
    statePath?: string
  ): string | null {
    // First check for auto-generated _k field (always takes priority)
    if (item._k && typeof item._k === "string") {
      return item._k;
    }

    if (!statePath || !this.rangeIdKeys[statePath]) {
      return null;
    }

    const keyPosStr = this.rangeIdKeys[statePath];
    return item[keyPosStr] || null;
  }

  private findItemIndexByKey(
    items: any[],
    key: string,
    statics: any[],
    statePath?: string
  ): number {
    return items.findIndex(
      (item: any) => this.getItemKey(item, statics, statePath) === key
    );
  }

  /**
   * Merges changes into a range item using deep merge to preserve statics.
   * When the server sends partial updates like {"5": {"0": "new text"}},
   * we need to merge this into the existing item's field 5, not replace it.
   * Shallow spread would lose the statics ({"s": [...]}) that the client has cached.
   */
  private mergeRangeItem(item: any, changes: any, statePath: string): any {
    return this.deepMergeTreeNodes(item, changes, `${statePath}.item`);
  }
}
